import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Link, useParams } from "react-router";
import accountStore from "../store/accountStore";
import { Spinner } from "../../../components/ui/Spinner";
import { getOrderStatus } from "../../../types/order";
import dayjs from "dayjs";
import { baseURL } from "../../../libs/http";
import { isMobile } from "react-device-detect";
import { InformationPopover } from "../../../components/ui";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatNumberWithSpaces } from "../../../utils/tools";

const OrderDetails = () => {
  const searchParams = useParams();

  const InfoRow = ({
    title,
    content,
    isLoading = false,
  }: {
    title: string;
    content: string;
    isLoading?: boolean;
  }) => {
    return (
      <div className="flex flex-col gap-[10px]">
        <span
          className={`font-semibold inline-flex items-center gap-[10px] ${
            isMobile ? "text-[14px]" : "text-[16px]"
          } tracking-[-0.02em] leading-[100%] text-[#474A51]`}
        >
          {title}
          {title == "Статус заказа" && !isLoading && (
            <InformationPopover
              title="Статус заказа"
              content={
                <ul>
                  <li>
                    В обработке — заказ отправлен, проверяем, все ли в порядке;
                  </li>
                  <li>
                    Готов к выдаче — заказ обработан, товар есть в наличии и
                    ждет вас в выбранном магазине;
                  </li>
                  <li>Выполнен — заказ успешно оплачен и получен;</li>
                  <li>Отменен — заказ отменен.</li>
                </ul>
              }
            ></InformationPopover>
          )}
        </span>
        <span
          className={`font-semibold ${
            isMobile ? "text-[14px]" : "text-[16px]"
          } tracking-[-0.02em] leading-[100%] text-[#232323]`}
        >
          {isLoading ? (
            <Skeleton
              width={isMobile ? 100 : 150}
              height={isMobile ? 16 : 20}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
          ) : (
            content
          )}
        </span>
      </div>
    );
  };

  const ProductRowSkeleton = () => {
    if (!isMobile) {
      return (
        <div className="flex flex-row justify-between max-h-[85px] items-center border-b border-solid border-[#C6C6C8] pb-[15px]">
          <div className="flex flex-row gap-[15px] items-center w-full">
            <Skeleton
              width={98}
              height={70}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
            <div className="flex flex-col gap-2 flex-1">
              <Skeleton
                width="80%"
                height={20}
                baseColor="#f3f3f3"
                highlightColor="#ecebeb"
              />
              <Skeleton
                width="30%"
                height={16}
                baseColor="#f3f3f3"
                highlightColor="#ecebeb"
              />
            </div>
          </div>
          <div className="flex flex-col items-end">
            <Skeleton
              width={80}
              height={20}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
            <Skeleton
              width={40}
              height={16}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
              className="mt-1"
            />
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-[10px] max-h-[100px] items-center border-b border-solid border-[#C6C6C8] pb-[10px]">
        <div className="flex flex-row gap-[10px] h-[63px] items-center w-full">
          <Skeleton
            width={78}
            height={56}
            baseColor="#f3f3f3"
            highlightColor="#ecebeb"
          />
          <div className="flex flex-col flex-1 gap-2">
            <Skeleton
              width="70%"
              height={16}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
            <Skeleton
              width="30%"
              height={14}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
          </div>
        </div>
        <Skeleton
          width={60}
          height={16}
          baseColor="#f3f3f3"
          highlightColor="#ecebeb"
          className="self-end"
        />
      </div>
    );
  };

  const ProductRow = ({
    name,
    quantity,
    price,
    refKey,
  }: {
    name: string;
    quantity: number;
    price: number;
    refKey: string;
  }) => {
    if (!isMobile) {
      return (
        <div
          key={refKey}
          className="flex flex-row justify-between max-h-[85px] last:border-0 last:pb-0 items-center border-b border-solid border-[#C6C6C8] pb-[15px] cursor-pointer"
          onClick={() => window.open(`/product/${refKey}`)}
        >
          <div className="flex flex-row gap-[15px] items-center">
            <img
              src={`${baseURL}/image/${refKey}`}
              alt=""
              loading="lazy"
              decoding="async"
              className="object-contain max-h-[70px] w-[98px]"
            />
            <span className="font-semibold text-[16px] tracking-[-0.02em] leading-[100%] text-[#232323] self-start text-wrap max-w-[520px]">
              {name}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-[16px] tracking-[-0.02em] leading-[100%] text-[#232323]">
              ₽ {price * quantity}
            </span>
            <span className="text-right leading-[19px] text-[13px] text-[#828282] tracking-[-0.02em] font-medium">
              {quantity} шт
            </span>
          </div>
        </div>
      );
    }
    return (
      <div
        key={refKey}
        className="flex flex-col gap-[10px] max-h-[100px] last:border-0  last:pb-0 items-center border-b border-solid border-[#C6C6C8] pb-[10px] cursor-pointer"
        onClick={() => window.open(`/product/${refKey}`)}
      >
        <div className="flex flex-row gap-[10px] h-[63px] items-center">
          <div className="relative w-[78px] h-[56px] flex-shrink-0 flex items-center justify-center">
            <img
              src={`${baseURL}/image/${refKey}`}
              alt=""
              loading="lazy"
              decoding="async"
              className="max-w-full max-h-full object-contain"
            />
          </div>

          <div className="flex flex-col">
            <span className="font-semibold text-[12px] tracking-[-0.02em] leading-[100%] text-[#232323] self-start text-wrap max-w-[90%]">
              {name}
            </span>
            <span className="text-left leading-[19px] text-[11px] text-[#828282] tracking-[-0.02em] font-medium">
              {quantity} шт
            </span>
          </div>
        </div>
        <span className="self-end text-[14px] font-semibold tracking-[-0.02em] leading-[100%] text-[#232323]">
          ₽ {price * quantity}
        </span>
      </div>
    );
  };

  useEffect(() => {
    if (searchParams.id) {
      accountStore.fetchOrderDetails(searchParams.id);
    }
  }, []);

  const isLoading = accountStore.isLoading;
  const hasData = accountStore?.orderDetails;

  return (
    <div
      className={`flex flex-col ${
        isMobile ? "gap-[25px] mt-[10px]" : "gap-[30px]"
      }  w-full`}
    >
      <Link
        to={"/profile"}
        className={`inline-flex items-center gap-[10px] font-semibold leading-[100%] ${
          isMobile ? "text-[16px] text-[#383181]" : "text-[20px] text-[#232323]"
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height={isMobile ? "16" : "18"}
          viewBox="0 0 11 18"
          fill="none"
        >
          <path
            d="M0 8.76953C0.00976562 8.36914 0.146484 8.03711 0.46875 7.72461L7.98828 0.371094C8.23242 0.117188 8.53516 0 8.90625 0C9.63867 0 10.2246 0.576172 10.2246 1.30859C10.2246 1.66992 10.0781 2.00195 9.81445 2.26562L3.13477 8.75977L9.81445 15.2734C10.0781 15.5371 10.2246 15.8594 10.2246 16.2305C10.2246 16.9629 9.63867 17.5488 8.90625 17.5488C8.54492 17.5488 8.23242 17.4219 7.98828 17.168L0.46875 9.81445C0.146484 9.50195 0 9.16992 0 8.76953Z"
            fill={isMobile ? "#383181" : "#232323"}
          />
        </svg>{" "}
        К списку заказов
      </Link>

      {/* Информация о заказе */}
      <div
        className={`w-full bg-white rounded-[20px] ${
          isMobile ? "p-[15px]" : "p-[25px]"
        } flex flex-col shadow-[0_2px_20px_#0000001a] gap-[20px]`}
      >
        <span
          className={` ${
            isMobile ? "text-[17px] font-bold" : "text-[20px] font-semibold"
          }  tracking-[-0.02em] text-[#232323]`}
        >
          {isLoading ? (
            <Skeleton
              width={200}
              height={isMobile ? 20 : 24}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
          ) : (
            "Информация о заказе"
          )}
        </span>
        <div
          className={`flex flex-row ${
            isMobile ? "!flex-col gap-[17px]" : "gap-[20px]"
          }`}
        >
          {!isMobile ? (
            <>
              <div className="flex flex-col gap-[20px] w-full">
                <InfoRow
                  title="Номер заказа"
                  content={hasData?.order_number || ""}
                  isLoading={isLoading}
                />
                <InfoRow
                  title="Статус заказа"
                  content={getOrderStatus(hasData?.status || "")}
                  isLoading={isLoading}
                />
                <InfoRow
                  title="Сумма"
                  content={`₽ ${formatNumberWithSpaces(
                    hasData?.total_amount || 0
                  )}`}
                  isLoading={isLoading}
                />
              </div>
              <div className="w-full flex flex-col gap-[20px]">
                <InfoRow
                  title="Дата заказа"
                  content={dayjs(hasData?.created_at).format(
                    "DD.MM.YYYY, HH:mm"
                  )}
                  isLoading={isLoading}
                />
                <InfoRow
                  title="Точка выдачи"
                  content={hasData?.delivery_point || ""}
                  isLoading={isLoading}
                />
              </div>
            </>
          ) : (
            <>
              <InfoRow
                title="Номер заказа"
                content={hasData?.order_number || ""}
                isLoading={isLoading}
              />
              <InfoRow
                title="Дата заказа"
                content={dayjs(hasData?.created_at).format("DD.MM.YYYY, HH:mm")}
                isLoading={isLoading}
              />
              <InfoRow
                title="Точка выдачи"
                content={hasData?.delivery_point || ""}
                isLoading={isLoading}
              />
              <InfoRow
                title="Статус заказа"
                content={getOrderStatus(hasData?.status || "")}
                isLoading={isLoading}
              />
              <InfoRow
                title="Сумма"
                content={`₽ ${formatNumberWithSpaces(
                  hasData?.total_amount || 0
                )}`}
                isLoading={isLoading}
              />
            </>
          )}
        </div>
      </div>

      {/* Информация о получателе */}
      <div
        className={`w-full bg-white rounded-[20px] ${
          isMobile ? "p-[15px]" : "p-[25px]"
        } flex flex-col shadow-[0_2px_20px_#0000001a] gap-[20px]`}
      >
        <span
          className={` ${
            isMobile ? "text-[17px] font-bold" : "text-[20px] font-semibold"
          }  tracking-[-0.02em] text-[#232323]`}
        >
          {isLoading ? (
            <Skeleton
              width={220}
              height={isMobile ? 20 : 24}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
          ) : (
            "Информация о получателе"
          )}
        </span>
        <div
          className={`flex flex-row ${
            isMobile ? "!flex-col gap-[17px]" : "gap-[20px]"
          }`}
        >
          <div className="flex flex-col gap-[20px] w-full">
            <InfoRow
              title="Получатель"
              content={`${hasData?.customer_name || ""}`}
              isLoading={isLoading}
            />
          </div>
          <div className="w-full flex flex-col gap-[20px]">
            <InfoRow
              title="Телефон получателя"
              content={hasData?.customer_phone || ""}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Состав заказа */}
      <div
        className={`w-full bg-white rounded-[20px] ${
          isMobile ? "p-[15px] mb-[10px]" : "p-[25px] mb-[5px]"
        } flex flex-col shadow-[0_2px_20px_#0000001a] gap-[20px]`}
      >
        <span
          className={`inline-flex items-center gap-[2px] ${
            isMobile ? "text-[17px] font-bold" : "text-[20px] font-semibold"
          }  tracking-[-0.02em] text-[#232323]`}
        >
          {isLoading ? (
            <Skeleton
              width={150}
              height={isMobile ? 20 : 24}
              baseColor="#f3f3f3"
              highlightColor="#ecebeb"
            />
          ) : (
            "Состав заказа"
          )}
        </span>
        <div className="flex flex-col gap-[15px]">
          {isLoading
            ? // Скелетоны для продуктов при загрузке
              Array.from({ length: 3 }).map((_, index) => (
                <ProductRowSkeleton key={`skeleton-${index}`} />
              ))
            : hasData?.items?.map((item) => (
                <ProductRow
                  key={item.id}
                  name={item.name}
                  quantity={item.quantity}
                  price={item.price}
                  refKey={item.product_ref_key}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

export default observer(OrderDetails);
